import { DownOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { rpnActions } from '@nxsys/store/slices/rpn/reducer/rpnSlice';
import { GetRPNListParams, RPN } from '@nxsys/store/slices/rpn/types';
import { getTaxWeekTaxYear } from '@nxsys/utils';
import { Dropdown } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionsHeader } from '../components/ActionsHeader';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { getColumns } from './columns';
import moment from 'moment';
import { csvConfigExport } from '@nxsys/utils/export';
import { ExportToCsv } from 'export-to-csv';
import { showToast } from '@nxsys/utils/toast';
import AppCardTabListContainer from '@nxsys/components/AppCardTabListContainer';
import RPNDetailModal from '../components/RPNDetailModal';
import UpdateRPNModal from '../components/UpdateRPNModal';
import { resolveFilterCompanyIds } from '@nxsys/utils/companyScope';

type Props = {
  cbAfterAction?: () => void;
  isDashboard?: boolean;
};

const RPNListing: React.FC<Props> = ({ cbAfterAction, isDashboard }) => {
  const { messages, formatMessage } = useTypedIntl();

  const tabs = [
    { tab: messages['rpn.rpnUpdates'], key: 'rpnUpdates' },
    { tab: messages['rpn.errors'], key: 'rpnErrors' },
  ];

  const taxYear = getTaxWeekTaxYear().taxYear;

  const dispatch = useDispatch<AppDispatch>();
  const { rpnList } = useSelector((state: AppState) => state.rpnStore);
  const { loading, list, pagination, queryParams } = rpnList;
  const { isBureauEmployer, user } = useSelector(
    (state: AppState) => state.appStore,
  );

  const [selectedRows, setSelectedRows] = useState<RPN[]>([]);
  const [activeTab, setActiveTab] = useState(tabs[0]?.key);
  const [RPNModal, setRPNModal] = useState({
    open: false,
    data: null,
  });
  const [updateRPNModal, setUpdateRPNModal] = useState({
    open: false,
  });
  const [keyRefreshTab, setKeyRefreshTab] = useState(0);

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle prefix={messages['rpn.title']} separator="" title={''} />
    );
  }, [messages]);

  const renderRightContent = () => {
    const menuProps = {
      items: [
        {
          key: '2',
          label: messages['rpn.exportSelected'],
          onClick: handleExportSelected,
        },
        {
          key: '3',
          label: messages['rpn.exportAll'],
          onClick: handleExportAll,
        },
      ],
    };

    return (
      <div className="flex items-center gap-2">
        <AppButton
          variant="outlined"
          name={messages['rpn.updateRPN']}
          onClick={toggleUpdateRPNModal}
        />
        <Dropdown menu={menuProps}>
          <AppButton
            type="primary"
            name={messages['common.bulkAction']}
            rightIcon={<DownOutlined />}
          />
        </Dropdown>
      </div>
    );
  };

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      taxYears: [taxYear],
      isSuccess: activeTab === tabs[0]?.key,
    });
  }, [activeTab]);

  const handleFetchData = (params: GetRPNListParams) => {
    // Resolve after merging, so a page or tab change keeps the applied employer
    const merged = { ...queryParams, ...params, isLatest: true };
    merged.companyIds = resolveFilterCompanyIds(
      merged.companyIds,
      isBureauEmployer,
      user?.companyUserIds,
    );
    dispatch(rpnActions.getRPNList(merged));
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleExportExcel = (data: RPN[]) => {
    const dataExport = data.map((item) => {
      const { subcontractor, rpns, createdAt, employerRegistrationNumber } =
        item;

      let yearlyCutOffTotal = 0;
      rpns?.[0]?.taxRates?.forEach((i) => {
        if (i?.yearlyRateCutOff)
          yearlyCutOffTotal = yearlyCutOffTotal + i?.yearlyRateCutOff;
      });

      return {
        dateReceived: createdAt ? moment(createdAt).format('DD/MM/YYYY') : '',
        dateEffective:
          rpns && rpns[0]
            ? moment(rpns[0]?.effectiveDate).format('DD/MM/YYYY')
            : '',
        employeeName: `${subcontractor?.forename || '_'} ${subcontractor?.surname || '_'}`,
        ppsNumber: rpns && rpns[0] ? rpns[0]?.employeeID?.employeePpsn : '',
        taxCredits: rpns && rpns[0] ? rpns[0]?.yearlyTaxCredits : 0,
        exemption:
          rpns && rpns[0]
            ? rpns[0]?.uscStatus === 'ORDINARY'
              ? 'NOT_EXEMPT'
              : 'EXEMPT'
            : '',
        COPoints0dot5:
          rpns && rpns[0] && rpns[0]?.uscRates[0]
            ? rpns[0]?.uscRates[0]?.yearlyUSCRateCutOff || ''
            : '',
        COPoints2:
          rpns && rpns[0] && rpns[0]?.uscRates[0]
            ? rpns[0]?.uscRates[1]?.yearlyUSCRateCutOff || ''
            : '',
        COPoints4:
          rpns && rpns[0] && rpns[0]?.uscRates[0]
            ? rpns[0]?.uscRates[2]?.yearlyUSCRateCutOff || ''
            : '',
        calculationBasis:
          rpns && rpns[0] ? rpns[0]?.incomeTaxCalculationBasis : '',
        annualCutOff: yearlyCutOffTotal,
        LPT: rpns && rpns[0] ? rpns[0]?.lptToDeduct : '',
        employmentID: rpns && rpns[0] ? rpns[0]?.employeeID?.employmentID : '',
        employerRegistrationNumber: employerRegistrationNumber || '',
      };
    });
    const headersExport = [
      messages['rpn.dateReceived'],
      messages['rpn.dateEffective'],
      messages['rpn.employeeName'],
      messages['rpn.ppsNumber'],
      messages['rpn.taxCredits'],
      messages['rpn.exemption'],
      formatMessage({ id: 'rpn.annualCutOffPercent' }, { percent: 0.5 }),
      formatMessage({ id: 'rpn.annualCutOffPercent' }, { percent: 2 }),
      formatMessage({ id: 'rpn.annualCutOffPercent' }, { percent: 3 }),
      messages['rpn.calculationBasis'],
      messages['rpn.annualCutOff'],
      'LPT',
      messages['rpn.employmentId'],
      messages['rpn.employerRegistrationNumber'],
    ];
    const exportName = csvConfigExport(
      messages['rpn.rpnUpdateExport'],
      headersExport,
    );
    const csvExporter = new ExportToCsv(exportName);
    csvExporter.generateCsv(dataExport);

    showToast({
      type: 'success',
      message: messages['common.exportSuccess'],
    });
  };

  const handleExportSelected = () => {
    if (!selectedRows || selectedRows.length === 0) {
      showToast({
        type: 'warning',
        message: messages['common.selectAtLeastOneToExport'],
      });
      return;
    }
    handleExportExcel(selectedRows);
  };

  const handleExportAll = async () => {
    if (!pagination.totalDocs) {
      showToast({ type: 'warning', message: messages['common.noDataToExport'] });
      return;
    }
    const res = await dispatch(
      rpnActions.getRPNList({
        ...queryParams,
        page: 0,
        size: pagination.totalDocs,
        search: undefined,
        taxYears: undefined,
        ...(!isBureauEmployer ? { companyIds: undefined }: {}),
        effectiveDate: undefined,
        endDate: undefined,
        rpnIssueDate: undefined,
        updatedAt: undefined,
      }),
    ).unwrap();
    handleExportExcel(res.data);
  };

  const toggleRPNModal = () =>
    setRPNModal({ ...RPNModal, open: !RPNModal.open });

  const handleOpenRPNModal = (data: any) => {
    setRPNModal({
      open: true,
      data,
    });
  };

  const toggleUpdateRPNModal = () => {
    if (updateRPNModal.open) {
      handleFetchData({ ...queryParams });
    }
    setUpdateRPNModal({ ...updateRPNModal, open: !updateRPNModal.open });
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setKeyRefreshTab((prev) => prev + 1);
  };

  const getRowClassName = (record: any) => {
    if (!record?.isSuccess) {
      return 'error-row';
    }

    return '';
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.effectiveDate ||
    !!queryParams?.endDate ||
    !!queryParams?.rpnIssueDate ||
    !!queryParams?.updatedAt ||
    (!!queryParams?.companyIds && queryParams?.companyIds.length > 0) ||
    (!!queryParams?.taxYears && queryParams?.taxYears.length > 0);

  const renderContent = () => {
    return (
      <>
        {isDashboard && (
          <div className="flex justify-end px-4 pt-2">
            {renderRightContent()}
          </div>
        )}
        <AppCardTabListContainer
          tabList={tabs}
          activeTab={activeTab}
          handleTabChange={handleTabChange}
          key={keyRefreshTab}
          {...(isDashboard && { className: '!border-none' })}
        >
          <AppsHeader>
            <ActionsHeader onFilter={handleFetchData} />
          </AppsHeader>
          <AppsContent>
            <AppTableContainer
              rowKey="id"
              data={list ?? []}
              loading={false}
              page={pagination.current}
              total={pagination.totalDocs}
              pageSize={pagination.pageSize}
              scroll={{ x: activeTab === tabs[1]?.key ? 4500 : 4200, y: 700 }}
              titleEmpty={messages['rpn.empty']}
              columns={getColumns({
                messages,
                formatMessage,
                onDownload: handleExportExcel,
                isErrorTab: activeTab === tabs[1]?.key,
                onClickView: handleOpenRPNModal,
              })}
              rowClassName={getRowClassName}
              onChangePagination={onChangePagination}
              rowSelection={{
                type: 'checkbox',
                onChange: (_: React.Key[], selectedRows: any[]) => {
                  setSelectedRows(selectedRows);
                },
              }}
              noDataType={isFiltering ? 'noDataFound' : 'noDataCreated'}
              addNewIcon={null}
              addNewBtnName={messages['rpn.updateRPN']}
              addNewBtnOnClick={toggleUpdateRPNModal}
            />
          </AppsContent>
        </AppCardTabListContainer>
        <RPNDetailModal
          isOpen={RPNModal.open}
          toggle={toggleRPNModal}
          data={RPNModal.data}
        />
        <UpdateRPNModal
          isOpen={updateRPNModal.open}
          toggle={toggleUpdateRPNModal}
          cbAfterAction={cbAfterAction}
        />
      </>
    );
  };

  if (isDashboard) {
    return renderContent();
  }

  return (
    <AppsContainer
      fullView
      type="bottom"
      loading={loading}
      title={renderTitle()}
      rightContent={renderRightContent()}
    >
      <AppCard>{renderContent()}</AppCard>
    </AppsContainer>
  );
};

export default RPNListing;
